<script>
import closeIconSrc from './icons/close-icon.svg';
import sessionIconSrc from './icons/session-icon.svg';
import upgradeCrownIconSrc from './icons/upgrade-crown.svg';
import Message from './Message.vue';
import Sending from './Sending.vue';
import Session from './Session.vue';

export default {
    components: {
        Session,
        Message,
        Sending
    },
    props: {
        featureConfig: Object,
        partnerConfig: Object,
        usage: Object,
        fullname: String,
        username: String,
        isFullscreen: Boolean,
        isSending: Boolean,
        currentSession: {
            default() {
                return ''
            }
        },
        sessions: {
            default() {
                return []
            }
        },
        messages: {
            default() {
                return []
            }
        }
    },
    data() {
        return {
            closeIconSrc,
            sessionIconSrc,
            upgradeCrownIconSrc,
            showSessions: false,
        }
    },
    computed: {
        config() {
            if (this.featureConfig.is_upgraded) {
                return this.featureConfig.upgrade?.config
            }
            return this.featureConfig.config
        },
        info() {
            return this.featureConfig.info
        },
        metadata() {
            // fix user context in chat session
            // const firstUserContent = this.messages.find(message => message.user.userId !== 'chatbot')?.content
            // let firstMetadata = null

            // try {
            //     const contentJSON = JSON.parse(firstUserContent)
            //     firstMetadata = contentJSON?.metadata || contentJSON?.context
            // } catch { }

            // return firstMetadata

            // allow change user context in chat session
            return null;
        },
        questionLeft() {
            const frequencyUsageMap = {
                "daily": this.usage.daily,
                "weekly": this.usage.weekly,
                "monthly": this.usage.monthly,
                "yearly": this.usage.yearly
            }
            const usage = frequencyUsageMap[this.config?.frequency]
            if (typeof this.config?.limit_question_send !== 'number' || typeof usage !== 'number') return 0;
            if (this.config?.limit_question_send === 0) return null;

            return Math.max(this.config.limit_question_send - usage, 0)
        },
        sessionQuestionLeft() {
            const botMessageCount = this.messages?.filter(message => message.user.userId === 'chatbot').length
            if (typeof this.config?.limit_interaction_each_session !== 'number' || typeof botMessageCount !== 'number') return 0;
            if (this.config?.limit_interaction_each_session === 0) return null;

            return Math.max(this.config.limit_interaction_each_session - botMessageCount, 0)
        },
        characterLimit() {
            return this.config?.limit_keyword_questions
        },
        isOverTime() {
            if (!this.messages.length) return false;
            // check if the last message is over 24h
            return Date.now() - this.messages[this.messages.length - 1].createdAt * 1000 > 24 * 60 * 60 * 1000
        },
        frequencyLabel() {
            const frequencyTranslationKey = {
                daily: 'ai_tutor_daily',
                weekly: 'ai_tutor_weekly',
                monthly: 'ai_tutor_monthly',
                yearly: 'ai_tutor_yearly',
            }[this.config?.frequency]

            if (!frequencyTranslationKey) return '';

            return this.$t(frequencyTranslationKey)
        }
    },
    methods: {
        toggleShowSession() {
            this.showSessions = !this.showSessions

            if (!this.showSessions) return;
            if (!this.$refs.chatBody) return;

            setTimeout(() => {
                if (!this.$refs.chatBody) return;

                const selected = this.$refs.chatBody.querySelector('.selected')
                selected?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }, 100)
        },
        handleSessionChange(sessionId) {
            window.boChatbot?.changeSession(sessionId)
            this.showSessions = false
        },
        handleNewSession() {
            window.boChatbot?.startConversation(this.fullname, this.username)
            this.showSessions = false
        },
        handleUpgradeFeature() {
            if (typeof appHandler !== 'undefined') {
                appHandler.postMessage('upgrade_ai_home_tutor')
            } else {
                this.$router.push({ name: 'upgrade_feature', query: { feature_code: 'ai_home_tutor' } })
                this.$emit('close')
            }
        },
        handleUpgradePackage() {
            if (typeof appHandler !== 'undefined') {
                appHandler.postMessage('upgrade_package')
            } else {
                this.$router.push({ name: 'upgrade_account' })
                this.$emit('close')
            }
        },
        scrollToBottom() {
            if (!this.$refs.chatBody) return;

            setTimeout(() => {
                if (!this.$refs.chatBody) return;

                this.$refs.chatBody.scrollTo({
                    top: this.$refs.chatBody.scrollHeight,
                    behavior: 'smooth'
                })
            }, 100)

            this.$refs.sending?.focus()
        }
    }
}
</script>

<template>
    <div class="chat-wrapper" :class="{ 'fullscreen': isFullscreen }">
        <div class="chat-header">
            <div class="chat-sessions" @click="toggleShowSession">
                <img :src="sessionIconSrc" class="chat-sessions-icon" />
            </div>
            <div class="chat-title">
                {{ partnerConfig?.chatBotName }}
            </div>
            <div v-if="!isFullscreen" class="close-button" @click="$emit('close')">
                <img :src="closeIconSrc" class="close-icon" />
            </div>
        </div>
        <div ref="chatBody" class="chat-body">
            <session v-if="showSessions" :sessions="sessions" :current-session="currentSession"
                @change="handleSessionChange" @new="handleNewSession" />
            <message v-else :messages="messages" :is-sending="isSending" :is-over-time="isOverTime"
                :question-left="questionLeft" :session-question-left="sessionQuestionLeft" :metadata="metadata"
                :info="info" :scroll-to-bottom="scrollToBottom" :partnerConfig="partnerConfig" />
            <div v-if="isSending" class="chat-thinking">
                Thinking...
            </div>
        </div>
        <div v-if="!showSessions" class="chat-sending">
            <template v-if="questionLeft === 0 && !featureConfig.is_upgraded && featureConfig.upgrade?.enable">
                <div class="chat-upgrade-limit" v-html="$t('ai_tutor_upgrade_limit', { frequency: frequencyLabel })">
                </div>
                <button class="chat-upgrade" @click="handleUpgradeFeature">
                    <span>{{ $t('ai_tutor_upgrade_now') }}</span>
                    <img :src="upgradeCrownIconSrc" class="chat-upgrade-icon" />
                </button>
            </template>
            <template v-else-if="questionLeft === 0 && featureConfig.upgrade_package_level_id">
                <div class="chat-upgrade-limit" v-html="$t('ai_tutor_upgrade_limit', { frequency: frequencyLabel })">
                </div>
                <button class="chat-upgrade" @click="handleUpgradePackage">
                    <span>{{ $t('ai_tutor_upgrade_now') }}</span>
                    <img :src="upgradeCrownIconSrc" class="chat-upgrade-icon" />
                </button>
            </template>
            <sending v-else ref="sending" :question-left="questionLeft" :session-question-left="sessionQuestionLeft"
                :character-limit="characterLimit" :is-sending="isSending" :is-over-time="isOverTime"
                :metadata="metadata" :info="info" :config="config" :frequency-label="frequencyLabel" />
        </div>
    </div>
</template>

<style scoped>
.chat-wrapper {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    width: 30rem;
    height: 70vh;
    border: 1px solid #E0E0E0;
    border-radius: 1rem;
    background-color: #fff;
    box-shadow: 0.25rem 0.25rem 0px 0px #0000001A;
    padding: 0.5rem;
    z-index: 99999;
    display: flex;
    flex-direction: column;
    line-height: 1.5;
}

.chat-wrapper.fullscreen {
    width: 100%;
    height: 100%;
    bottom: 0;
    right: 0;
    border-radius: 0;
}

.chat-header {
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.95rem 1.25rem;
    background-color: #F4F3EF;
    gap: 0.5rem;
}

.chat-sessions {
    width: 2rem;
    height: 2rem;
    background-color: #3CB7FD;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
}

.chat-sessions-icon {
    width: 0.9rem;
    height: auto;
}

.chat-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #0D30A0;
    flex-grow: 1;
}

.close-button {
    width: 2rem;
    height: 2rem;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    cursor: pointer;
    flex-shrink: 0;
}

.close-icon {
    width: 0.85rem;
    height: auto;
}

.chat-body {
    height: 100%;
    overflow: auto;
    overscroll-behavior: contain;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    margin-top: 1.2rem;
    margin-bottom: 0.5rem;
}

.chat-thinking {
    text-align: center;
    font-style: italic;
    font-size: 0.8rem;
    color: #333;
    margin-top: 1rem;
}

.chat-sending {
    padding: 0.4rem 0.5rem;
}

.chat-upgrade {
    outline: none;
    border: none;
    background-color: #F08635;
    color: #fff;
    border-radius: 2rem;
    box-shadow: 0 0.125rem 0 0 #FFFFFF33 inset, 0.25rem 0.25rem 0 0 #00000012;
    width: 100%;
    padding: 0.6rem 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 0.5rem;
    font-weight: 700;
    font-size: 1.1rem;
}

.chat-upgrade-icon {
    width: 1.7rem;
}

.chat-upgrade-limit {
    font-size: 0.65rem;
    font-style: italic;
    margin: 0 1.5rem 0.25rem;
    line-height: 1.2;
    color: #EA2A37;
    text-align: center;
}
</style>